import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  faCheck,
  faChevronDown,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TRIGGER_DENTAL_CHART_EXTRACTION } from "@graphql/mutations/TriggerDentalChartExtraction";
import { useWatchUserWsEvent } from "@hooks";
import { useOnboardingMilestones } from "@hooks/use-onboarding-milestones";
import {
  selectPendingProposalForSection,
  usePendingWritingCompanionProposals,
} from "@hooks/use-pending-writing-companion-proposals";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import { animated, useTransition } from "@react-spring/web";
import { isHandledEmailFlowError } from "@src/utils/emailFlowErrors";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState, useEffect, useRef } from "react";
import { useForm, useWatch, useFormState } from "react-hook-form";

import ClientSummary from "./ClientSummary";
import ClientSummaryFreePlanState from "./ClientSummary/ClientSummaryFreePlanState";
import CustomForm from "./CustomForm";
import DentalChart from "./DentalChart";
import { KeyPatientInfoFormFields } from "./KeyPatientInfoFormFields";
import { NoteHeader } from "./NoteHeader";
import SOAPForm from "./SOAPForm";
import { usePremiumFeature } from "../../.././../hooks";
import SEND_EXPORT_NOTE_EMAIL from "../../../../graphql/mutations/SendExportNoteEmail";
import Twemoji from "../../../../Twemoji";
import { AttachmentsPreview } from "../../../attachments/AttachmentsPreview";
import { alert } from "../../../common/Alert";
import AttachmentSelection from "../../../common/inputs/AttachmentSelection";
import ExportModal from "../../../email/ExportModal";
import PromptFeedbackWidget from "../../common/PromptFeedbackWidget";
import ExportNoteSection from "../../ExportNoteSection";
import LegacySOAPForm from "../../legacy/LegacySOAPForm";

const DELETE_ATTACHMENT = gql`
  mutation deleteAttachment($attachmentUuid: String) {
    deleteAttachment(attachmentUuid: $attachmentUuid) {
      ok
    }
  }
`;

export function NoteForm({
  note,
  refetchNote,
  isQuicknote,
  isSuperSOAP,
  isTemplate = false,
  isScribeEdit,
  getValues,
  patientUUID,
  control,
  onSaveAsDraft,
  onSubmitNote,
  onUnmarkAsDone,
}) {
  const [exportExpanded, setExportExpanded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDentalChartDone, setShowDentalChartDone] =
    useState(false);
  const [isDoneAnimating, setIsDoneAnimating] = useState(false);
  const [lastExtractedCustomText, setLastExtractedCustomText] =
    useState(null);
  const currentCustomText = useWatch({ control, name: "customText" });

  const { dirtyFields } = useFormState({ control });
  const isFieldDirty = !!dirtyFields?.customText;

  const isChartDirty =
    isFieldDirty && currentCustomText !== lastExtractedCustomText;
  const prevDentalChartStatus = useRef(note?.dentalChartStatus);
  const doneAnim = useTransition(showDentalChartDone, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 250 },
  });

  useEffect(() => {
    const prev = prevDentalChartStatus.current;
    prevDentalChartStatus.current = note?.dentalChartStatus;

    if (
      prev === "PROCESSING" &&
      note?.dentalChartStatus === "UP_TO_DATE"
    ) {
      const showTimer = setTimeout(() => {
        setShowDentalChartDone(true);
        setIsDoneAnimating(true);
      }, 0);
      const hideTimer = setTimeout(
        () => setShowDentalChartDone(false),
        1200,
      );
      const doneTimer = setTimeout(
        () => setIsDoneAnimating(false),
        1500,
      );
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
        clearTimeout(doneTimer);
      };
    }
  }, [note?.dentalChartStatus]);
  const { useLegacyNotes, releaseWebOnboardingMilestones } =
    useFlags();
  const { completeMilestone } = useOnboardingMilestones();
  const shouldUseCustomForm = useWatch({
    control,
    name: "shouldUseCustomForm",
  });
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();

  const { proposals: pendingProposals } =
    usePendingWritingCompanionProposals();
  const pendingClientSummaryProposal =
    selectPendingProposalForSection(
      pendingProposals,
      note?.uuid,
      "clientSummary",
    );
  const [clientSummaryExpanded, setClientSummaryExpanded] =
    useState(false);
  const [expandedForProposalUuid, setExpandedForProposalUuid] =
    useState(null);
  if (
    pendingClientSummaryProposal &&
    pendingClientSummaryProposal.uuid !== expandedForProposalUuid
  ) {
    setExpandedForProposalUuid(pendingClientSummaryProposal.uuid);
    setClientSummaryExpanded(true);
  }

  const isDentalChart =
    note?.jobType === "dental_record" ||
    !!note?.dentalChartData ||
    (note?.jobType === "template" &&
      note?.autoTemplates?.dentalChart === true);

  const shouldShowFreePlanState = !hasFeatureAccess(
    UserPermissions.CLIENT_SUMMARY_ENABLED,
  );

  const shouldShowFeedbackWidget = hasFeatureAccess(
    UserPermissions.NOTE_FEEDBACK_ENABLED,
  );

  const emailMethods = useForm();
  const { control: emailControl, handleSubmit: emailHandleSubmit } =
    emailMethods;

  const [sendExportNoteEmail] = useMutation(SEND_EXPORT_NOTE_EMAIL);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const [triggerDentalChartExtraction] = useMutation(
    TRIGGER_DENTAL_CHART_EXTRACTION,
  );

  useWatchUserWsEvent({
    onMessage: (message) => {
      if (message?.data?.note_uuid === note?.uuid) {
        refetchNote?.();
      }
    },
    eventName: "note_update",
  });

  const handleUpdateChart = async () => {
    if (!note?.uuid) return;
    const valueAtClick = getValues("customText");
    try {
      const { data } = await triggerDentalChartExtraction({
        variables: { noteUuid: note.uuid },
      });
      if (!data?.triggerDentalChartExtraction?.ok) {
        alert(
          "error",
          "Unable to update the dental chart. Please try again.",
        );
        return;
      }
      setLastExtractedCustomText(valueAtClick);
      refetchNote?.();
    } catch {
      alert(
        "error",
        "Failed to update the dental chart. Please try again.",
      );
    }
  };

  const handleDeleteAttachment = async (attachmentUuid) => {
    try {
      await deleteAttachment({
        variables: { attachmentUuid },
      });
      alert("success", "Attachment deleted.");
      refetchNote?.();
    } catch {
      alert(
        "error",
        "Failed to delete attachment. Please try again.",
      );
    }
  };

  const onEmailSend = async (data) => {
    try {
      await sendExportNoteEmail({
        variables: {
          toEmail: data?.toEmail,
          grantedNoteUuid: note?.uuid,
          isForAssistant: false,
          specialInstructions: data?.specialInstructions,
          areAttachmentsIncluded: data?.includeAttachments,
          shouldEmbedEmailExports:
            data?.exportType === "embeddedText",
        },
      });
      alert("success", `Email sent to ${data?.toEmail}!`);
      setShowEmailModal(false);
    } catch (error) {
      console.error("Error sending export email:", error);
      if (!isHandledEmailFlowError(error)) {
        alert("error", "An error occurred. Please try again later.");
      }
    }
  };

  return (
    <div className="mb-5" data-testid="note-form-container">
      <ExportModal
        shouldShowModal={showEmailModal}
        hideModal={() => setShowEmailModal(false)}
        handleSubmit={emailHandleSubmit}
        onSend={onEmailSend}
        control={emailControl}
        methods={emailMethods}
      />

      <NoteHeader
        note={note}
        refetchNote={refetchNote}
        control={control}
        isTemplate={isTemplate}
        isQuicknote={isQuicknote}
        isSuperSOAP={isSuperSOAP}
        isScribeEdit={isScribeEdit}
        patientUUID={patientUUID}
        onSaveAsDraft={onSaveAsDraft}
        onSubmitNote={onSubmitNote}
        onUnmarkAsDone={onUnmarkAsDone}
      />

      <div className="mb-5" data-testid="note-form-container-inner">
        {shouldUseCustomForm ? (
          <div className="flex flex-col md:flex-row md:items-start md:space-x-10 space-y-10 md:space-y-0">
            <CustomForm control={control} note={note} />
            {isDentalChart && (
              <div className="w-[300px] md:w-[400px] -mt-1">
                <DentalChart
                  record={
                    note?.autoTemplates?.dentalChart ||
                    note?.dentalChartData
                      ? undefined
                      : note?.customFormFields?.[0]?.textValue
                  }
                  dentalChartData={
                    note?.autoTemplates?.dentalChart ||
                    note?.dentalChartData
                      ? (note?.dentalChartData ?? null)
                      : null
                  }
                  status={
                    note?.autoTemplates?.dentalChart ||
                    note?.dentalChartData
                      ? (note?.dentalChartStatus ?? "UP_TO_DATE")
                      : undefined
                  }
                  actionSlot={
                    note?.autoTemplates?.dentalChart ? (
                      <div
                        className={
                          isChartDirty ||
                          note?.dentalChartStatus ===
                            "NEEDS_UPDATE" ||
                          note?.dentalChartStatus === "PROCESSING" ||
                          isDoneAnimating
                            ? ""
                            : "invisible"
                        }
                      >
                        {note?.dentalChartStatus === "PROCESSING" ? (
                          <button
                            disabled
                            className="w-full inline-flex items-center justify-center rounded-full font-medium bg-disabled opacity-80 text-white shadow-sm cursor-not-allowed py-sm px-md text-base"
                          >
                            <FontAwesomeIcon
                              icon={faSpinner}
                              className="animate-spin mr-2"
                            />
                            Updating Chart...
                          </button>
                        ) : isDoneAnimating ? (
                          <div className="relative">
                            <button
                              className="invisible w-full inline-flex items-center justify-center rounded-full font-medium bg-brand text-white py-sm px-md text-base"
                              tabIndex={-1}
                              aria-hidden
                            >
                              Update Chart
                            </button>
                            {doneAnim(
                              (styles, item) =>
                                item && (
                                  <animated.div
                                    style={styles}
                                    className="absolute inset-0 flex items-center justify-center gap-2 rounded-full bg-green-500 text-white font-medium text-base shadow-sm"
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                    Done!
                                  </animated.div>
                                ),
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={handleUpdateChart}
                            className="w-full inline-flex items-center justify-center rounded-full font-medium transition-colors bg-brand text-white shadow-sm hover:bg-[var(--surface-brand-hover)] py-sm px-md text-base"
                          >
                            Update Chart
                          </button>
                        )}
                      </div>
                    ) : note?.dentalChartData ? (
                      <div className="invisible">
                        <button className="w-full inline-flex items-center justify-center rounded-full font-medium bg-brand text-white py-sm px-md text-base">
                          Update Chart
                        </button>
                      </div>
                    ) : undefined
                  }
                />
              </div>
            )}
          </div>
        ) : useLegacyNotes ? (
          <LegacySOAPForm control={control} note={note} />
        ) : (
          <SOAPForm note={note} />
        )}
      </div>

      {shouldShowFeedbackWidget &&
        note?.jobType !== "dictation" &&
        note?.isAutoSoap &&
        !note?.isMarkedAsFailed && (
          <div className="relative h-12 mb-6">
            <PromptFeedbackWidget
              jobType={note?.jobType}
              noteUuid={note?.uuid}
              userUuid={note?.user?.uuid}
              feedback={note?.feedback}
              thumbsUp={note?.thumbsUp}
            />
          </div>
        )}

      <div className="py-5">
        {!isScribeEdit && !isTemplate && (
          <div className="flex flex-col space-y-sm">
            {note?.patient && (
              <div id="additionalInfo">
                <Accordion
                  defaultExpanded={false}
                  disableGutters={true}
                  sx={{
                    "&::before": {
                      backgroundColor: "transparent",
                    },
                    boxShadow:
                      "0px 0px 5px 0px var(--shadow-primary)",
                    borderRadius: "100px",
                    backgroundColor: "var(--surface-primary)",
                    marginTop: "10px",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <FontAwesomeIcon
                        className="dark:text-gray-300"
                        icon={faChevronDown}
                      />
                    }
                  >
                    <div>
                      <h4>
                        <Twemoji emoji="➕" /> Additional Key
                        Information
                      </h4>
                      <h6 className="text-secondary">
                        If you would like to include additional key
                        information regarding this patient, type it in
                        below and it will be added to their notebook.
                      </h6>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails>
                    <KeyPatientInfoFormFields
                      control={control}
                      keyHistory={note?.keyHistory}
                      keyMedications={note?.keyMedications}
                      vaccinationStatus={note?.vaccinationStatus}
                      weight={note?.weight}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            )}

            <div id="addAttachments">
              <Accordion
                defaultExpanded={false}
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="add-attachments-header">
                      <Twemoji emoji="📎" /> Add Attachments
                    </h4>
                    <h6 className="text-secondary">
                      Accepted file types: pictures, videos, PDFs,
                      documents.
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div id="addAttachments" className="my-2">
                    <AttachmentSelection
                      noteUuid={note?.uuid}
                      onUploadComplete={refetchNote}
                    />
                  </div>
                  {note?.attachments && (
                    <AttachmentsPreview
                      attachments={note?.attachments}
                      isCompactView={true}
                      attachmentPreviewText={
                        "Files attached to this note"
                      }
                      emptyStateText={
                        "No files are currently attached to this note."
                      }
                      onDelete={handleDeleteAttachment}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </div>

            <div id="noteEmailSection">
              <Accordion
                onChange={(_, expanded) =>
                  setExportExpanded(expanded)
                }
                defaultExpanded={false}
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="export-note-header">
                      <Twemoji emoji="📤" /> Export Note
                    </h4>
                    <h6 className="text-secondary">
                      Bring this note into your PIMS, or export it in
                      other ways.
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <ExportNoteSection
                    note={note}
                    getValues={getValues}
                    exportExpanded={exportExpanded}
                    onEmailClick={() => setShowEmailModal(true)}
                    onExportSuccess={
                      releaseWebOnboardingMilestones
                        ? () => completeMilestone("note_exported")
                        : undefined
                    }
                  />
                </AccordionDetails>
              </Accordion>
            </div>

            <div
              id="clientSummary"
              data-testid="client-summary-accordion"
            >
              <Accordion
                expanded={clientSummaryExpanded}
                onChange={(_, isExpanded) =>
                  setClientSummaryExpanded(isExpanded)
                }
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="client-summary-header">
                      <Twemoji emoji="🏠" />{" "}
                      <span className="ml-1">Client Summary</span>
                    </h4>

                    <h6 className="text-secondary">
                      Automatically generate a summary for your client
                      based on your note!
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  {!shouldShowFreePlanState ? (
                    <ClientSummary
                      note={note}
                      refetchNote={refetchNote}
                    />
                  ) : (
                    <ClientSummaryFreePlanState
                      noteUuid={note?.uuid}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
